import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow, prism } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Box, Heading, Spinner, Alert, AlertIcon, Link as ChakraLink, Button, Flex, Card, CardBody } from '@chakra-ui/react';
import { AuthContext } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import mermaid from 'mermaid';

function DocumentationPage() {
  const { role } = useContext(AuthContext);
  const { isDarkMode } = useTheme();
  const [currentFile, setCurrentFile] = useState('index.md');
  const [navigationHistory, setNavigationHistory] = useState(['index.md']);
  const [markdown, setMarkdown] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const markdownRef = useRef();

  // Initialize mermaid when component mounts or theme changes
  useEffect(() => {
    mermaid.initialize({ 
      startOnLoad: true,
      theme: isDarkMode ? 'dark' : 'default',
      securityLevel: 'loose',
      fontFamily: 'sans-serif'
    });
  }, [isDarkMode]);

  // Render mermaid diagrams after markdown renders or changes
  useEffect(() => {
    if (markdownRef.current && !loading) {
      try {
        // Small delay to ensure DOM is ready
        const timer = setTimeout(() => {
          mermaid.contentLoaded();
          
          // Ensure diagrams stay contained on small screens
          document.querySelectorAll('.mermaid svg').forEach(svg => {
            if (svg) {
              svg.style.maxWidth = '100%';
              svg.style.height = 'auto';
            }
          });
        }, 100);
        
        return () => clearTimeout(timer);
      } catch (error) {
        console.error('Mermaid render error:', error);
      }
    }
  }, [markdown, loading, isDarkMode]);

  const fetchMarkdown = useCallback((filename) => {
    setLoading(true);
    setError(null);
    fetch(`/docs/${filename}`)
      .then(response => {
        if (!response.ok) {
          if (response.status === 404) {
             throw new Error(`File not found: /docs/${filename}`);
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.text();
      })
      .then(text => {
        setMarkdown(text);
        setLoading(false);
      })
      .catch(e => {
        console.error(`Error fetching documentation file ${filename}:`, e);
        setError(`Could not load documentation file: ${filename}. ${e.message}`);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchMarkdown(currentFile);
  }, [currentFile, fetchMarkdown]);

  const handleLinkClick = (href) => {
     if (href && !href.startsWith('http') && href.endsWith('.md')) {
       const targetFile = href.startsWith('./') ? href.substring(2) : href;
       setNavigationHistory(prev => [...prev, currentFile]);
       setCurrentFile(targetFile);
       return true;
     }
     return false;
  };

  const handleGoBack = () => {
    if (navigationHistory.length > 0) {
      const previousFile = navigationHistory[navigationHistory.length - 1];
      setCurrentFile(previousFile);
      setNavigationHistory(prev => prev.slice(0, -1));
    } else {
      setCurrentFile('index.md');
    }
  };

  // Handle clicks on links within the rendered HTML
  useEffect(() => {
    const handleDocumentClick = (e) => {
      // Check if it's a link click
      let target = e.target;
      while (target && target.tagName !== 'A') {
        target = target.parentElement;
      }
      
      if (target && target.tagName === 'A') {
        const href = target.getAttribute('href');
        if (href && href.endsWith('.md') && !href.startsWith('http')) {
          e.preventDefault();
          handleLinkClick(href);
        }
      }
    };
    
    document.addEventListener('click', handleDocumentClick);
    return () => document.removeEventListener('click', handleDocumentClick);
  }, []);

  // Add custom CSS for the markdown content
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .markdown-container {
        overflow-x: hidden !important;
        overflow: hidden !important;
        max-width: 100%;
        width: 100%;
        word-break: break-word !important;
        box-sizing: border-box !important;
        display: block !important;
      }
      
      .markdown-content {
        max-width: 100% !important;
        width: 100% !important;
        color: ${isDarkMode ? 'var(--color-text)' : 'var(--color-text)'};
        overflow-wrap: break-word !important;
        word-wrap: break-word !important;
        word-break: break-word !important;
        box-sizing: border-box !important;
      }
      
      .markdown-content h1, 
      .markdown-content h2, 
      .markdown-content h3, 
      .markdown-content h4, 
      .markdown-content h5, 
      .markdown-content h6 {
        color: ${isDarkMode ? 'var(--color-text)' : 'var(--color-text)'};
        font-weight: bold;
        margin-top: 1.5em;
        margin-bottom: 0.5em;
        max-width: 100% !important;
        overflow-wrap: break-word !important;
        white-space: normal !important;
      }
      
      .markdown-content p {
        margin-bottom: 1em;
        line-height: 1.6;
        max-width: 100% !important;
        overflow-wrap: break-word !important;
        white-space: normal !important;
      }
      
      .markdown-content ul,
      .markdown-content ol {
        margin-left: 1.5em;
        margin-bottom: 1em;
        padding-right: 1em;
        max-width: calc(100% - 1.5em) !important;
        width: auto !important;
        list-style-position: inside;
      }
      
      .markdown-content ol {
        list-style-type: decimal !important;
      }
      
      .markdown-content li {
        margin-bottom: 0.5em;
        overflow-wrap: break-word !important;
        white-space: normal !important;
      }
      
      .markdown-content a {
        color: ${isDarkMode ? 'var(--color-accent)' : 'var(--color-accent)'};
        word-break: break-all !important;
        overflow-wrap: break-word !important;
      }
      
      .markdown-content code:not(.prettyprint) {
        background-color: ${isDarkMode ? 'var(--color-secondary)' : 'var(--color-secondary)'};
        padding: 0.2em 0.4em;
        border-radius: 3px;
        font-size: 85%;
        color: ${isDarkMode ? 'var(--color-accent)' : 'var(--color-accent)'};
        overflow-wrap: break-word !important;
        white-space: pre-wrap !important;
        word-break: break-word !important;
        max-width: 100% !important;
      }
      
      .markdown-content pre {
        background-color: ${isDarkMode ? 'var(--color-secondary)' : 'var(--color-secondary)'};
        padding: 1.5em;
        margin: 1.5em 0;
        border-radius: 0.5rem;
        overflow-x: auto !important;
        width: 100% !important;
        border: 1px solid ${isDarkMode ? 'var(--color-secondary)' : 'var(--color-secondary)'};
        max-width: 100% !important;
      }
      
      .markdown-content pre code {
        white-space: pre-wrap !important;
        overflow-wrap: anywhere !important;
        word-break: break-word !important;
        max-width: 100% !important;
      }
      
      .markdown-content blockquote {
        border-left: 4px solid ${isDarkMode ? 'var(--color-accent)' : 'var(--color-accent)'};
        padding-left: 1em;
        font-style: italic;
        color: ${isDarkMode ? 'var(--color-text)' : 'var(--color-text)'};
        margin: 1em 0;
        max-width: calc(100% - 1em) !important;
        width: auto !important;
        background-color: ${isDarkMode ? 'rgba(0, 209, 219, 0.05)' : 'rgba(0, 173, 181, 0.05)'};
        padding: 1rem;
        border-radius: 0.375rem;
      }
      
      .markdown-content table {
        width: 100%;
        margin: 1em 0;
        border-collapse: collapse;
        display: block !important;
        overflow-x: auto !important;
        max-width: 100% !important;
        white-space: nowrap;
      }
      
      .markdown-content th,
      .markdown-content td {
        border: 1px solid ${isDarkMode ? 'var(--color-secondary)' : 'var(--color-secondary)'};
        padding: 0.5em;
        text-align: left;
        min-width: 6em;
      }
      
      .markdown-content th {
        background-color: ${isDarkMode ? 'var(--color-secondary)' : 'var(--color-secondary)'};
      }
      
      .markdown-content img {
        max-width: 100% !important;
        height: auto !important;
        display: block !important;
        margin: auto;
        border-radius: 0.375rem;
      }
      
      .markdown-content .mermaid {
        text-align: center;
        margin: 0;
        padding: 1rem;
        background-color: ${isDarkMode ? 'var(--color-secondary)' : 'var(--color-secondary)'};
        border-radius: 0.375rem;
        max-width: 100% !important;
        overflow: hidden !important;
        box-sizing: border-box !important;
      }
      
      .markdown-content .mermaid svg {
        max-width: 100% !important; 
        min-width: 0 !important;
        height: auto !important;
        width: auto !important;
      }
      
      /* Force containment on all possible elements */
      .markdown-content > * {
        max-width: 100% !important;
        overflow-wrap: break-word !important;
        word-wrap: break-word !important;
        box-sizing: border-box !important;
      }
      
      /* Target syntax highlighter specifically */
      .markdown-content [class*="language-"] code,
      .markdown-content .token,
      .markdown-content pre[class*="language-"],
      .markdown-content pre code {
        font-size: 0.9rem !important;
        line-height: 1.5 !important;
      }
      
      .doc-section {
        margin-bottom: 2rem;
        border-radius: 0.5rem;
        overflow: hidden;
      }
      
      /* Even more aggressive mobile optimizations */
      @media screen and (max-width: 768px) {
        .markdown-content * {
          max-width: 100% !important;
          overflow-x: hidden !important;
          word-break: break-word !important;
        }
        
        .markdown-content pre,
        .markdown-content code {
          font-size: 0.7rem !important;
          white-space: pre-wrap !important;
        }
        
        .markdown-content table {
          font-size: 0.85rem !important;
          width: 100% !important;
          display: block !important;
          overflow-x: auto !important;
        }
        
        .markdown-content pre {
          padding: 1rem !important;
          margin: 1rem 0 !important;
        }
        
        .markdown-content .prism-code,
        .markdown-content pre code,
        .markdown-content div[class*="language-"] {
          font-size: 0.7rem !important;
        }
        
        .markdown-content img, 
        .markdown-content svg {
          max-width: 100% !important;
          height: auto !important;
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, [isDarkMode]);

  if (role !== 'admin') {
    return (
      <Box p={5}>
        <Alert status="error">
          <AlertIcon />
          Access Denied. You must be an administrator to view this page.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box p={5} display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <Spinner size="xl" color={isDarkMode ? "var(--color-accent)" : "var(--color-accent)"} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={5}>
        <Card 
          variant="elevated" 
          bg={isDarkMode ? "var(--color-primary)" : "var(--color-primary)"}
          color={isDarkMode ? "var(--color-text)" : "var(--color-text)"}
          boxShadow="var(--shadow-md)"
          borderRadius="lg"
        >
          <CardBody>
            <Alert status="error" flexDirection="column" alignItems="center" justifyContent="center" textAlign="center">
              <AlertIcon boxSize="40px" mr={0} />
              {error}
              {currentFile !== 'index.md' && (
                <Button 
                  mt={4} 
                  size="sm" 
                  onClick={() => setCurrentFile('index.md')}
                  bg={isDarkMode ? "var(--color-accent)" : "var(--color-accent)"}
                  color="white"
                  _hover={{ brightness: "110%" }}
                >
                  Back to Index
                </Button>
              )}
            </Alert>
          </CardBody>
        </Card>
      </Box>
    );
  }

  // Split the content by headers to create separate cards
  const createContentSections = () => {
    // Simple regex to split by headers
    const sections = markdown.split(/(?=^#{1,3} )/m);
    
    if (sections.length <= 1) {
      // If no headers or just one section, return entire content in one card
      return (
        <Card 
          variant="elevated" 
          bg={isDarkMode ? "var(--color-primary)" : "var(--color-primary)"}
          color={isDarkMode ? "var(--color-text)" : "var(--color-text)"}
          boxShadow="var(--shadow-md)"
          borderRadius="lg"
          mb={4}
          className="doc-section"
        >
          <CardBody>
            <ReactMarkdownWrapper content={markdown} isDarkMode={isDarkMode} />
          </CardBody>
        </Card>
      );
    }
    
    // Create a card for each section
    return sections.map((section, index) => {
      if (!section.trim()) return null;
      
      return (
        <Card 
          key={index}
          variant="elevated" 
          bg={isDarkMode ? "var(--color-primary)" : "var(--color-primary)"}
          color={isDarkMode ? "var(--color-text)" : "var(--color-text)"}
          boxShadow="var(--shadow-md)"
          borderRadius="lg"
          mb={4}
          className="doc-section"
          transition="transform 0.2s, box-shadow 0.2s"
          _hover={{
            transform: "translateY(-2px)",
            boxShadow: "var(--shadow-lg)"
          }}
        >
          <CardBody>
            <ReactMarkdownWrapper content={section} isDarkMode={isDarkMode} />
          </CardBody>
        </Card>
      );
    });
  };
  
  const ReactMarkdownWrapper = ({ content, isDarkMode }) => {
    return (
      <ReactMarkdown
        children={content}
        remarkPlugins={[remarkGfm]}
        components={{
          code: ({node, inline, className, children, ...props}) => {
            const match = /language-(\w+)/.exec(className || '');
            
            // For mermaid code blocks, render a div with mermaid class
            if (!inline && match && match[1] === 'mermaid') {
              return (
                <div className="mermaid">
                  {String(children).replace(/\n$/, '')}
                </div>
              );
            }
            
            return !inline && match ? (
              <SyntaxHighlighter
                style={isDarkMode ? tomorrow : prism}
                language={match[1]}
                PreTag="div"
                className="rounded-md my-4"
                wrapLines={true}
                wrapLongLines={true}
                customStyle={{
                  borderRadius: '0.375rem',
                  
                  border: `1px solid ${isDarkMode ? 'var(--color-secondary)' : 'var(--color-secondary)'}`,
                  backgroundColor: isDarkMode ? 'var(--color-secondary)' : 'var(--color-secondary)',
                  maxWidth: '100%',
                  overflowX: 'auto',
                  fontSize: '0.9rem',
                  lineHeight: '1.5'
                }}
                {...props}
              >
                {String(children).replace(/\n$/, '')}
              </SyntaxHighlighter>
            ) : (
              <code
                className={`font-mono text-sm px-1.5 py-0.5 rounded ${className}`}
                style={{
                  backgroundColor: isDarkMode ? 'var(--color-secondary)' : 'var(--color-secondary)',
                  color: isDarkMode ? 'var(--color-accent)' : 'var(--color-accent)'
                }}
                {...props}
              >
                {children}
              </code>
            );
          },
          a: ({node, ...props}) => {
            const href = props.href;
            if (href && !href.startsWith('http') && href.endsWith('.md')) {
              return (
                <ChakraLink 
                  {...props} 
                  onClick={(e) => {
                    e.preventDefault();
                    handleLinkClick(href);
                  }}
                  color={isDarkMode ? "var(--color-accent)" : "var(--color-accent)"}
                  _hover={{ textDecoration: 'underline' }}
                  overflowWrap="break-word"
                  wordBreak="break-word"
                />
              );
            }
            return (
              <ChakraLink 
                {...props} 
                isExternal 
                color={isDarkMode ? "var(--color-accent)" : "var(--color-accent)"}
                overflowWrap="break-word" 
                wordBreak="break-word"
              />
            );
          },
          p: ({children, ...props}) => (
            <p style={{maxWidth: '100%', overflowWrap: 'break-word'}} {...props}>
              {children}
            </p>
          ),
          img: ({node, ...props}) => (
            <img 
              style={{maxWidth: '100%', height: 'auto'}} 
              loading="lazy"
              {...props} 
            />
          ),
          table: ({children, ...props}) => (
            <div style={{maxWidth: '100%', overflowX: 'auto'}}>
              <table {...props}>{children}</table>
            </div>
          )
        }}
      />
    );
  };

  return (
    <Box 
      p={{base: 3, md: 5}}
      className="bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark transition-colors duration-300"
      maxWidth="100%"
      overflowX="hidden"
      width="100%"
    >
      <Card 
        variant="elevated" 
        bg={isDarkMode ? "var(--color-primary)" : "var(--color-primary)"}
        color={isDarkMode ? "var(--color-text)" : "var(--color-text)"}
        boxShadow="var(--shadow-md)"
        borderRadius="lg"
        mb={5}
      >
        <CardBody>
          <Flex direction="row" justify="space-between" align="center" wrap="wrap" gap={2}>
            <Heading as="h1" size="lg" className="text-text-light dark:text-text-dark">
              Technical Documentation
            </Heading>
            
            {currentFile !== 'index.md' && (
              <Button 
                size="sm" 
                onClick={() => setCurrentFile('index.md')}
                bg={isDarkMode ? "var(--color-accent)" : "var(--color-accent)"}
                color={isDarkMode ? "white" : "white"}
                _hover={{ brightness: "110%" }}
                leftIcon={
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" style={{width: "20px", height: "20px"}}>
                    <path fillRule="evenodd" d="M10.707 3.293a1 1 0 010 1.414L6.414 9H17a1 1 0 110 2H6.414l4.293 4.293a1 1 0 01-1.414 1.414l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                }
              >
                Main Index
              </Button>
            )}
          </Flex>
        </CardBody>
      </Card>
      
      <Box className="markdown-container" ref={markdownRef}>
        <div className="markdown-content">
          {createContentSections()}
          
          {currentFile !== 'index.md' && (
            <Card 
              variant="elevated" 
              bg={isDarkMode ? "var(--color-primary)" : "var(--color-primary)"}
              color={isDarkMode ? "var(--color-text)" : "var(--color-text)"}
              boxShadow="var(--shadow-md)"
              borderRadius="lg"
              mt={4}
              p={4}
            >
              <Button
                onClick={handleGoBack}
                bg={isDarkMode ? "var(--color-accent)" : "var(--color-accent)"}
                color="white"
                _hover={{ brightness: "110%" }}
                leftIcon={
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" style={{width: "20px", height: "20px"}}>
                    <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                  </svg>
                }
              >
                Go Back
              </Button>
            </Card>
          )}
        </div>
      </Box>
    </Box>
  );
}

export default DocumentationPage; 